import React, { useState, useEffect, useCallback, useRef } from 'react';
import { expensesService, bankAccountsService } from '../services/api';
import { LineChart, Line, BarChart, Bar, ComposedChart, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceLine, Cell } from 'recharts';
import DreView from './DreView';
import AnalyticalFlowView from './AnalyticalFlowView';
import { useAviso } from './shared/Aviso';
import { useEstadoDaCarga, mensagemDoErroDeCarga, textos } from '../design';
import { TONE_TEXT } from '../utils/statusColors';
import { CartaoDoTopo, barraDoSinal, contornoDoSinal, contornoDoSaldoDeConta } from './shared/lista/PecasDaLista';
import { saldosDasContas, mesesSemMovimento, fraseDosMesesSemMovimento, fraseDaRecargaQueFalhou, rotuloDoPeriodo } from '../utils/fluxoDeCaixa';

const fmt = (v: any) => {
  const n = parseFloat(String(v));
  return isNaN(n) ? '0,00' : n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const monthNames = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

const fmtMonth = (m: string) => {
  if (!m) return '—';
  const [year, month] = m.split('-');
  return `${monthNames[parseInt(month) - 1] || month}/${year}`;
};

type ViewMode = 'cash' | 'accrual';
type Granularity = 'daily' | 'monthly';
type SubView = 'cashflow' | 'analytical' | 'dre';

const fmtDayLabel = (d: string) => {
  if (!d) return '—';
  const [, m, day] = d.split('-');
  return `${day}/${m}`;
};

// D7: a conta sem cor cadastrada levava o laranja do acento (#f97316) na barra; neutro (slate-500), sem dizer nada.
const COR_DA_CONTA_SEM_COR = '#64748b';

/**
 * O PADRÃO DA CENTRAL NO FLUXO (23/10/2026, continuação do padrão das listas). Superfície neutra e valor em branco, com o
 * sinal (N1). O que alterna de sinal (o resultado, o saldo do dia ou do mês, o total) leva a barra pelo sinal: verde no
 * positivo, vermelha no negativo. O saldo de conta (quase sempre positivo) só leva a barra no negativo. Recebido em verde e
 * pago em vermelho em toda célula era decoração: o cabeçalho da coluna já diz. O gráfico mantém a paleta de sempre (G2,
 * decisão do Heitor): entrada verde, saída vermelha, a linha do acumulado azul.
 */
const CARTAO = 'bg-central-cartao rounded-central border border-central-borda';
const COR = { entrada: '#4ade80', saida: '#f87171', linha: '#60a5fa', grade: '#334155', eixo: '#94a3b8' };
const TH = 'px-4 py-3 text-xs font-semibold text-central-texto-fraco uppercase';
const VALOR = 'px-4 py-3 text-right whitespace-nowrap tabular-nums text-central-texto';

export default function CashFlowTab() {
  const [subView, setSubView] = useState<SubView>('cashflow');
  const [loading, setLoading] = useState(true);
  const [cashFlow, setCashFlow] = useState<any>(null);
  const [dailyCashFlow, setDailyCashFlow] = useState<any>(null);
  const [bankAccounts, setBankAccounts] = useState<any[]>([]);
  const [months, setMonths] = useState(1);
  const [dreLoading, setDreLoading] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('cash');
  const [granularity, setGranularity] = useState<Granularity>('daily');

  // D1 (11/10/2026): a carga calava. Na primeira falha os quatro cards diziam R$ 0,00 (inclusive o saldo em contas); na
  // recarga que falhava (‹ › ou Diário/Mensal) o cabeçalho passava a dizer o período NOVO sobre os números do anterior.
  // Agora: cada leitura leva o seu período, a resposta de um período que saiu da tela é descartada, e a tela diz de qual
  // período e de quando são os números. As contas têm carga própria: a falha delas não derruba o fluxo.
  const avisar = useAviso();
  const cargaFluxo = useEstadoDaCarga();
  const cargaContas = useEstadoDaCarga();
  const rodarFluxo = cargaFluxo.rodar;
  const rodarContas = cargaContas.rodar;
  const periodoPedido = useRef(months);
  periodoPedido.current = months;
  const [periodoDosDados, setPeriodoDosDados] = useState<number | null>(null);
  const loadData = useCallback(async () => {
    setLoading(true);
    const pedido = months;
    await Promise.all([
      rodarFluxo(async () => {
        const [cf, daily] = await Promise.all([
          expensesService.cashFlow(pedido),
          expensesService.cashFlow(pedido, 'daily'),
        ]);
        if (periodoPedido.current !== pedido) return;
        setCashFlow(cf);
        setDailyCashFlow(daily);
        setPeriodoDosDados(pedido);
      }),
      rodarContas(async () => {
        const banks = await bankAccountsService.getAll();
        setBankAccounts(Array.isArray(banks) ? banks : []);
      }),
    ]);
    setLoading(false);
  }, [months, rodarFluxo, rodarContas]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleExportDre = async () => {
    setDreLoading(true);
    try {
      // `cashFlow.months` é SEMPRE a série de CAIXA. Usar isso aqui ignorava o
      // viewMode: em Competência, o PDF do DRE saía com o intervalo dos meses de
      // caixa. E a variável local `cfMonths` sombreava a do escopo externo —
      // enquanto o `disabled` do botão testava a de fora, já filtrada. Nome
      // próprio para não voltar a sombrear.
      const exportRange = viewMode === 'cash'
        ? (cashFlow?.months || [])
        : (cashFlow?.accrualMonths || cashFlow?.months || []);
      if (exportRange.length === 0) return;
      const startMonth = exportRange[0].month;
      const endMonth = exportRange[exportRange.length - 1].month;
      const blob = await expensesService.getDreReport(startMonth, endMonth);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `DRE_${startMonth}_a_${endMonth}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      // D6: calava; o clique não fazia nada.
      avisar(`Não consegui gerar o PDF do DRE: ${mensagemDoErroDeCarga(e)}`, 'error');
    } finally {
      // `finally`: o `return` antecipado (lista vazia) pulava esta linha e
      // deixava o botão presa em "Gerando...", sem erro visível. Só não
      // aparecia porque o botão fica disabled nesse estado.
      setDreLoading(false);
    }
  };

  const temFluxo = periodoDosDados !== null;
  if (loading && !temFluxo && cargaFluxo.estado !== 'erro') {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
        <span className="ml-3 text-slate-400">Carregando...</span>
      </div>
    );
  }

  const cfMonthsRaw = viewMode === 'cash' ? (cashFlow?.months || []) : (cashFlow?.accrualMonths || cashFlow?.months || []);
  // Esconde meses sem movimento nenhum. O `accumulatedBalance` vem do BACKEND e
  // NÃO é recalculado aqui.
  //
  // Antes havia um `let acc = 0` que refazia a soma sobre a lista já filtrada.
  // Isso criava DOIS acumulados diferentes na mesma tela: a tabela diária usava
  // o número do backend e a mensal usava este, recalculado. Alternar
  // Diário/Mensal no mesmo período dava valores incompatíveis, sem nada na tela
  // explicando a diferença.
  //
  // Filtrar não corrompe o acumulado: mês sem receita e sem despesa tem balanço
  // zero, então não contribui para a soma — o valor do mês seguinte já é o
  // mesmo com ou sem ele na lista.
  const cfMonths = cfMonthsRaw.filter(
    (m: any) => (parseFloat(m.revenue) || 0) !== 0 || (parseFloat(m.expenses) || 0) !== 0,
  );

  // Daily data
  const cfDays: any[] = viewMode === 'cash' ? (dailyCashFlow?.days || []) : (dailyCashFlow?.accrualDays || dailyCashFlow?.days || []);

  // Summary based on current granularity
  const summaryData = granularity === 'daily' ? cfDays : cfMonths;
  const totalRevenue = summaryData.reduce((sum: number, m: any) => sum + (parseFloat(m.revenue) || 0), 0);
  const totalExpenses = summaryData.reduce((sum: number, m: any) => sum + (parseFloat(m.expenses) || 0), 0);
  const netResult = totalRevenue - totalExpenses;
  // D2: caixa = contas ativas que não são de investimento; o aplicado vai ao lado, e as inativas ficam fora.
  const saldos = saldosDasContas(bankAccounts);
  const temContas = cargaContas.atualizadoEm !== null;
  const totalBankBalance = saldos.caixa;
  // D1: os números na tela são de outro período (a recarga falhou ou ainda está chegando).
  const dadosDeOutroPeriodo = temFluxo && periodoDosDados !== months;
  const reais = (v: number) => (temFluxo ? `R$ ${fmt(v)}` : textos.numeroSemFonte);
  // D3: os meses sem movimento saem do gráfico; a tela diz quais foram.
  const semMovimento = granularity === 'monthly' ? fraseDosMesesSemMovimento(mesesSemMovimento(cfMonthsRaw), fmtMonth) : null;

  const viewLabels = {
    cash: { title: 'Regime de Caixa', subtitle: 'Baseado na data real de pagamento/recebimento', revenueLabel: 'Recebido', expenseLabel: 'Pago' },
    accrual: { title: 'Regime de Competência', subtitle: 'Baseado no mês de referência (faturamento/vencimento)', revenueLabel: 'Faturado', expenseLabel: 'Previsto' },
  };
  const vl = viewLabels[viewMode];

  return (
    <div className="space-y-6">
      {/* Sub-view tabs */}
      <div className="flex items-center gap-2 border-b border-central-borda pb-0" role="tablist">
        {[
          { key: 'cashflow' as SubView, label: 'Fluxo de Caixa' },
          { key: 'analytical' as SubView, label: 'Fluxo Analítico' },
          { key: 'dre' as SubView, label: 'DRE' },
        ].map(tab => (
          <button
            key={tab.key}
            role="tab"
            aria-selected={subView === tab.key}
            onClick={() => setSubView(tab.key)}
            className={`min-h-[44px] px-4 text-[15px] border-b-[3px] -mb-px transition-colors ${
              subView === tab.key
                ? 'border-central-texto text-central-texto font-semibold'
                : 'border-transparent text-central-texto-fraco font-medium hover:text-central-texto'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {subView === 'analytical' && <AnalyticalFlowView />}
      {subView === 'dre' && <DreView />}

      {subView === 'cashflow' && <>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-central-texto">Fluxo de Caixa</h2>
          <p className="text-central-texto-fraco text-sm mt-1">{vl.subtitle} — {rotuloDoPeriodo(temFluxo ? (periodoDosDados as number) : months)}</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Granularity toggle. Cor (D7): segmentado neutro da casa, não o azul sólido. */}
          <div className="flex rounded-lg border border-slate-700 overflow-hidden">
            <button onClick={() => { setGranularity('daily'); setMonths(1); }} className={`px-3 py-2 text-xs font-medium transition-colors ${granularity === 'daily' ? 'bg-slate-600 text-white font-semibold' : 'bg-slate-800 text-slate-400 hover:text-white'}`}>
              Diário
            </button>
            <button onClick={() => { setGranularity('monthly'); setMonths(6); }} className={`px-3 py-2 text-xs font-medium transition-colors ${granularity === 'monthly' ? 'bg-slate-600 text-white font-semibold' : 'bg-slate-800 text-slate-400 hover:text-white'}`}>
              Mensal
            </button>
          </div>
          {/* View mode toggle */}
          <div className="flex rounded-lg border border-slate-700 overflow-hidden">
            <button onClick={() => setViewMode('cash')} className={`px-3 py-2 text-xs font-medium transition-colors ${viewMode === 'cash' ? 'bg-slate-600 text-white font-semibold' : 'bg-slate-800 text-slate-400 hover:text-white'}`}>
              Caixa
            </button>
            <button onClick={() => setViewMode('accrual')} className={`px-3 py-2 text-xs font-medium transition-colors ${viewMode === 'accrual' ? 'bg-slate-600 text-white font-semibold' : 'bg-slate-800 text-slate-400 hover:text-white'}`}>
              Competência
            </button>
          </div>
          <button onClick={handleExportDre} disabled={dreLoading || cfMonths.length === 0} className="px-4 py-2 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 text-sm font-medium disabled:opacity-50 flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
            {dreLoading ? 'Gerando...' : 'Exportar DRE'}
          </button>
          <div className="flex items-center gap-1">
            <button onClick={() => setMonths(m => { const opts = granularity === 'daily' ? [1, 2, 3] : [3, 6, 12, 24]; const i = opts.indexOf(m); return i > 0 ? opts[i - 1] : opts[0]; })} className="p-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-300 hover:text-white transition-colors">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
            </button>
            <span className="text-white text-sm font-medium min-w-[80px] text-center select-none">{months === 1 ? '1 mês' : `${months} meses`}</span>
            <button onClick={() => setMonths(m => { const opts = granularity === 'daily' ? [1, 2, 3] : [3, 6, 12, 24]; const i = opts.indexOf(m); return i < opts.length - 1 ? opts[i + 1] : opts[opts.length - 1]; })} className="p-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-300 hover:text-white transition-colors">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
            </button>
          </div>
        </div>
      </div>

      {/* D1: a carga que falha diz; os números de outro período dizem de qual e de quando são. */}
      {!temFluxo && cargaFluxo.estado === 'erro' && (
        <div role="alert" className="px-4 py-3 rounded-lg border border-red-600/50 text-sm text-red-300" data-testid="fluxo-que-nao-carregou">
          Não consegui carregar o fluxo de caixa: {cargaFluxo.erro}. Os números abaixo não foram lidos.
          <button onClick={loadData} className="ml-2 underline">Tentar de novo</button>
        </div>
      )}
      {dadosDeOutroPeriodo && (
        <div role="alert" className={`px-4 py-3 rounded-lg border border-amber-600/60 text-sm ${TONE_TEXT.warning}`} data-testid="fluxo-de-outro-periodo">
          {cargaFluxo.estado === 'erro'
            ? fraseDaRecargaQueFalhou(months, periodoDosDados as number, cargaFluxo.atualizadoEm || new Date().toISOString(), cargaFluxo.erro)
            : `Carregando ${rotuloDoPeriodo(months)}… Os números abaixo ainda são de ${rotuloDoPeriodo(periodoDosDados as number)}.`}
          {cargaFluxo.estado === 'erro' && <button onClick={loadData} className="ml-2 underline">Tentar de novo</button>}
        </div>
      )}

      {/* Os cartões do topo (padrão das listas): valor branco; o negativo leva a barra vermelha. */}
      <div className="grid grid-cols-4 gap-4">
        <CartaoDoTopo rotulo={`Total ${vl.revenueLabel}`} valor={<span data-kpi="recebido">{reais(totalRevenue)}</span>} />
        <CartaoDoTopo rotulo={`Total ${vl.expenseLabel}`} valor={reais(totalExpenses)} />
        <CartaoDoTopo rotulo="Resultado" valor={reais(netResult)} className={temFluxo ? contornoDoSinal(netResult) : ''} data-sinal={temFluxo ? Math.sign(netResult) : undefined} />
        <CartaoDoTopo
          rotulo="Caixa em Contas"
          valor={<span data-kpi="caixa">{temContas ? `R$ ${fmt(totalBankBalance)}` : textos.numeroSemFonte}</span>}
          className={temContas ? contornoDoSaldoDeConta(totalBankBalance) : ''}
          sub={temContas && saldos.contasAplicadas > 0
            ? <span data-testid="aplicado">Aplicado, fora do caixa: R$ {fmt(saldos.aplicado)} · posição sem data (veja em Contas)</span>
            : !temContas && cargaContas.estado === 'erro'
              ? <span className={TONE_TEXT.warning}>Não consegui ler as contas: {cargaContas.erro}</span>
              : undefined}
        />
      </div>

      {/* Reconciliação: o que passa pela conta e NÃO é receita nem despesa.
          Explica por que o saldo deste relatório não é o saldo do banco, em vez
          de embutir esses valores no fluxo — embuti-los contaria dinheiro duas
          vezes na antecipação de recebíveis (a liberação do borderô entra no
          extrato E a fatura antecipada entra de novo quando o boleto liquida). */}
      {(cashFlow?.naoOperacional || []).length > 0 && (
        <div className={`${CARTAO} p-5`}>
          <p className="text-central-texto text-sm font-semibold">Fora do fluxo, no período</p>
          <p className="text-slate-500 text-xs mt-0.5">
            Movimentam a conta mas não são receita nem despesa — é o que explica a diferença
            entre o resultado acima e o saldo em contas.
          </p>
          <div className="mt-3 space-y-1.5">
            {(cashFlow?.naoOperacional || []).map((n: any) => (
              <div key={n.categoria} className="flex items-center justify-between text-sm">
                <span className="text-slate-300">{n.categoria}</span>
                <span className="text-central-texto tabular-nums">
                  {n.liquido >= 0 ? '+' : '−'} R$ {fmt(Math.abs(n.liquido))}
                </span>
              </div>
            ))}
          </div>
          <p className="text-slate-500 text-[11px] mt-3 leading-relaxed">
            Transferência entre as próprias contas e estorno bancário não aparecem aqui: eles se
            anulam e não mudam o total. A antecipação de recebíveis é adiantamento, não receita
            nova — a receita entra pela fatura quando o boleto liquida.
          </p>
        </div>
      )}

      {/* Bank account balances */}
      {bankAccounts.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {bankAccounts.filter(b => b.isActive !== false).map((bank: any) => (
            <div key={bank.id} className={`${CARTAO} p-3 flex items-center gap-3 ${contornoDoSaldoDeConta(parseFloat(bank.currentBalance) || 0)}`}>
              <div className="w-2.5 h-8 rounded-full" style={{ backgroundColor: bank.color || COR_DA_CONTA_SEM_COR }} />
              <div>
                <p className="text-slate-300 text-sm font-medium">{bank.bankName}</p>
                <p className="font-bold text-central-texto tabular-nums">
                  R$ {fmt(bank.currentBalance)}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      {semMovimento && (
        <p className="text-xs text-slate-400" data-testid="meses-sem-movimento">{semMovimento}</p>
      )}

      {/* ===== DAILY VIEW ===== */}
      {granularity === 'daily' && (() => {
        const dailyChartData = cfDays.map((d: any) => ({
          name: fmtDayLabel(d.date),
          entradas: parseFloat(d.revenue) || 0,
          saidas: parseFloat(d.expenses) || 0,
          acumulado: parseFloat(d.accumulatedBalance) || 0,
        }));

        const tickInterval = dailyChartData.length <= 15 ? 0 : dailyChartData.length <= 30 ? 1 : Math.floor(dailyChartData.length / 15);

        const dailyTooltip = ({ active, payload, label }: any) => {
          if (!active || !payload?.length) return null;
          return (
            <div className="bg-central-elevado border border-central-borda rounded-lg p-3 shadow-lg">
              <p className="text-central-texto-fraco text-xs mb-2">{label}</p>
              {payload.map((entry: any, idx: number) => (
                <p key={idx} className="text-sm" style={{ color: entry.color }}>
                  {entry.name}: R$ {fmt(entry.value)}
                </p>
              ))}
            </div>
          );
        };

        return (
          <>
            {/* Daily ComposedChart */}
            {dailyChartData.length > 0 && (
              <div className={`${CARTAO} p-5`}>
                <h3 className="text-central-texto font-semibold mb-4">Movimentação Diária — {vl.revenueLabel} vs {vl.expenseLabel}</h3>
                <ResponsiveContainer width="100%" height={350}>
                  <ComposedChart data={dailyChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke={COR.grade} />
                    <XAxis dataKey="name" tick={{ fill: COR.eixo, fontSize: 11 }} interval={tickInterval} angle={-45} textAnchor="end" height={50} />
                    <YAxis yAxisId="left" tick={{ fill: COR.eixo, fontSize: 12 }} tickFormatter={(v: number) => `${(v / 1000).toFixed(0)}k`} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fill: COR.linha, fontSize: 12 }} tickFormatter={(v: number) => `${(v / 1000).toFixed(0)}k`} />
                    <Tooltip content={dailyTooltip} />
                    <Legend wrapperStyle={{ color: COR.eixo, fontSize: 12 }} />
                    <Bar yAxisId="left" dataKey="entradas" name={vl.revenueLabel} fill={COR.entrada} radius={[2, 2, 0, 0]} barSize={12} />
                    <Bar yAxisId="left" dataKey="saidas" name={vl.expenseLabel} fill={COR.saida} radius={[2, 2, 0, 0]} barSize={12} />
                    <Line yAxisId="right" type="monotone" dataKey="acumulado" name="Acumulado" stroke={COR.linha} strokeWidth={2} dot={false} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Daily table */}
            <div className={`${CARTAO} overflow-hidden`}>
              <table className="w-full text-sm">
                <thead className="bg-central-elevado">
                  <tr>
                    <th className={`${TH} text-left`}>Data</th>
                    <th className={`${TH} text-right`}>{vl.revenueLabel}</th>
                    <th className={`${TH} text-right`}>{vl.expenseLabel}</th>
                    <th className={`${TH} text-right`}>Saldo</th>
                    <th className={`${TH} text-right`}>Acumulado</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-central-borda">
                  {cfDays.length === 0 ? (
                    <tr><td colSpan={5} className="px-4 py-8 text-center text-central-texto-fraco">Nenhum dado disponível</td></tr>
                  ) : cfDays.map((d: any, idx: number) => {
                    const revenue = parseFloat(d.revenue) || 0;
                    const expense = parseFloat(d.expenses) || 0;
                    const balance = parseFloat(d.balance) || 0;
                    const accumulated = parseFloat(d.accumulatedBalance) || 0;

                    return (
                      <tr key={d.date || idx} className="hover:bg-central-elevado transition-colors" data-saldo={balance < 0 ? 'negativo' : balance > 0 ? 'positivo' : undefined}>
                        <td className={`px-4 py-3 text-central-texto font-medium ${barraDoSinal(balance)}`}>{fmtDayLabel(d.date)}</td>
                        <td className={VALOR}>{revenue > 0 ? `R$ ${fmt(revenue)}` : <span className="text-central-texto-fraco">—</span>}</td>
                        <td className={VALOR}>{expense > 0 ? `R$ ${fmt(expense)}` : <span className="text-central-texto-fraco">—</span>}</td>
                        <td className={`${VALOR} font-semibold`}>R$ {fmt(balance)}</td>
                        <td className={VALOR}>R$ {fmt(accumulated)}</td>
                      </tr>
                    );
                  })}
                </tbody>
                {cfDays.length > 0 && (
                  <tfoot className="bg-central-elevado border-t border-central-borda">
                    <tr>
                      <td className={`px-4 py-3 text-central-texto font-bold ${barraDoSinal(netResult)}`}>Total</td>
                      <td className={`${VALOR} font-bold`}>R$ {fmt(totalRevenue)}</td>
                      <td className={`${VALOR} font-bold`}>R$ {fmt(totalExpenses)}</td>
                      <td className={`${VALOR} font-bold`}>R$ {fmt(netResult)}</td>
                      <td className="px-4 py-3 text-right text-central-texto-fraco">—</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </>
        );
      })()}

      {/* ===== MONTHLY VIEW ===== */}
      {granularity === 'monthly' && (() => {
        const chartData = cfMonths.map((m: any) => {
          const [year, month] = (m.month || '').split('-');
          const label = monthNames[parseInt(month) - 1]?.substring(0, 3) || month;
          return {
            name: `${label}/${year?.substring(2)}`,
            receitas: parseFloat(m.revenue) || 0,
            despesas: parseFloat(m.expenses) || 0,
            saldo: parseFloat(m.balance) || 0,
          };
        });

        const customTooltip = ({ active, payload, label }: any) => {
          if (!active || !payload?.length) return null;
          return (
            <div className="bg-central-elevado border border-central-borda rounded-lg p-3 shadow-lg">
              <p className="text-central-texto-fraco text-xs mb-2">{label}</p>
              {payload.map((entry: any, idx: number) => (
                <p key={idx} className="text-sm" style={{ color: entry.color }}>
                  {entry.name}: R$ {fmt(entry.value)}
                </p>
              ))}
            </div>
          );
        };

        return (
          <>
            {/* Monthly Charts */}
            {cfMonths.length > 0 && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Line Chart */}
                <div className={`${CARTAO} p-5`}>
                  <h3 className="text-central-texto font-semibold mb-4">{vl.revenueLabel} vs {vl.expenseLabel}</h3>
                  <ResponsiveContainer width="100%" height={280}>
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke={COR.grade} />
                      <XAxis dataKey="name" tick={{ fill: COR.eixo, fontSize: 12 }} />
                      <YAxis tick={{ fill: COR.eixo, fontSize: 12 }} tickFormatter={(v: number) => `${(v / 1000).toFixed(0)}k`} />
                      <Tooltip content={customTooltip} />
                      <Legend wrapperStyle={{ color: COR.eixo, fontSize: 12 }} />
                      <Line type="monotone" dataKey="receitas" name={vl.revenueLabel} stroke={COR.entrada} strokeWidth={2} dot={{ fill: COR.entrada, r: 3 }} />
                      <Line type="monotone" dataKey="despesas" name={vl.expenseLabel} stroke={COR.saida} strokeWidth={2} dot={{ fill: COR.saida, r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                {/* Bar Chart */}
                <div className={`${CARTAO} p-5`}>
                  <h3 className="text-central-texto font-semibold mb-4">Saldo Mensal</h3>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke={COR.grade} />
                      <XAxis dataKey="name" tick={{ fill: COR.eixo, fontSize: 12 }} />
                      <YAxis tick={{ fill: COR.eixo, fontSize: 12 }} tickFormatter={(v: number) => `${(v / 1000).toFixed(0)}k`} />
                      <Tooltip content={customTooltip} />
                      <ReferenceLine y={0} stroke={COR.eixo} />
                      <Bar dataKey="saldo" name="Saldo" radius={[4, 4, 0, 0]}>
                        {chartData.map((entry: any, index: number) => (
                          <Cell key={index} fill={entry.saldo >= 0 ? COR.entrada : COR.saida} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* Monthly table */}
            <div className={`${CARTAO} overflow-hidden`}>
              <table className="w-full text-sm">
                <thead className="bg-central-elevado">
                  <tr>
                    <th className={`${TH} text-left`}>Mês</th>
                    <th className={`${TH} text-right`}>{vl.revenueLabel}</th>
                    <th className={`${TH} text-right`}>{vl.expenseLabel}</th>
                    <th className={`${TH} text-right`}>Saldo</th>
                    <th className={`${TH} text-right`}>Acumulado</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-central-borda">
                  {cfMonths.length === 0 ? (
                    <tr><td colSpan={5} className="px-4 py-8 text-center text-central-texto-fraco">Nenhum dado disponível</td></tr>
                  ) : cfMonths.map((m: any, idx: number) => {
                    const revenue = parseFloat(m.revenue) || 0;
                    const expense = parseFloat(m.expenses) || 0;
                    const balance = parseFloat(m.balance) || 0;
                    const accumulated = parseFloat(m.accumulatedBalance) || 0;

                    return (
                      <tr key={m.month || idx} className="hover:bg-central-elevado transition-colors" data-saldo={balance < 0 ? 'negativo' : balance > 0 ? 'positivo' : undefined}>
                        <td className={`px-4 py-3 text-central-texto font-medium ${barraDoSinal(balance)}`}>{fmtMonth(m.month)}</td>
                        <td className={VALOR}>R$ {fmt(revenue)}</td>
                        <td className={VALOR}>R$ {fmt(expense)}</td>
                        <td className={`${VALOR} font-semibold`}>R$ {fmt(balance)}</td>
                        <td className={VALOR}>R$ {fmt(accumulated)}</td>
                      </tr>
                    );
                  })}
                </tbody>
                {cfMonths.length > 0 && (
                  <tfoot className="bg-central-elevado border-t border-central-borda">
                    <tr>
                      <td className={`px-4 py-3 text-central-texto font-bold ${barraDoSinal(netResult)}`}>Total</td>
                      <td className={`${VALOR} font-bold`}>R$ {fmt(totalRevenue)}</td>
                      <td className={`${VALOR} font-bold`}>R$ {fmt(totalExpenses)}</td>
                      <td className={`${VALOR} font-bold`}>R$ {fmt(netResult)}</td>
                      <td className="px-4 py-3 text-right text-central-texto-fraco">—</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </>
        );
      })()}
      </>}
    </div>
  );
}
